import { JID } from "@xmpp/jid"
import type { TFunction } from "i18next"
import i18n from "@/lib/i18n/i18n"
import type { AccountChat } from "@/lib/accountChatsParser"
import { ChatEvent, MessageDisplayType } from "@/reducers/messagesSlice"
import ChatHelpers from "@/lib/chatHelpers"
import MessageHelpers from "@/lib/messageHelpers"
import { getTransicsMessagePresentation } from "@/lib/transicsMessagePresentation"

type LastEventTextInput = {
  event: ChatEvent | undefined
  myJid: JID
  accountChat: AccountChat | undefined
  /** An unsent draft wins over the archive: it is what the user left behind. */
  draftMessage?: string
  /** The caller's `t`, so the preview follows a language switch. */
  t?: TFunction
}

/** The one-line preview under a chat's name in the list. */
export const formatLastEventText = ({
  event,
  myJid,
  accountChat,
  draftMessage,
  t = i18n.t,
}: LastEventTextInput): string => {
  if (draftMessage?.length) {
    return t("chat.lastEvent.draft", { text: draftMessage })
  }

  if (!event) return ""

  if (event.displayType === MessageDisplayType.Reaction) {
    if (MessageHelpers.hasComeFromJid(event, myJid.toString())) {
      return t("chat.lastEvent.youReacted", { emojis: event.emojis })
    }

    return t("chat.lastEvent.reacted", { emojis: event.emojis })
  }

  if (event.retracted) {
    return t("chat.message.deleted")
  }

  if (event.file) {
    return t("chat.lastEvent.sentFile")
  }

  const transicsPresentation = getTransicsMessagePresentation(
    event,
    accountChat,
  )
  return (
    `${transicsPresentation?.body ?? event.body}` ||
    t("chat.lastEvent.fileAttachment")
  )
}

/** Slick AI signs itself, and a draft belongs to nobody but the user. */
export const resolveLastEventAuthorName = ({
  authorName,
  event,
  draftMessage,
}: {
  authorName: string
  event: ChatEvent | undefined
  draftMessage?: string
}): string => {
  if (draftMessage?.length) return ""
  if (event && ChatHelpers.isMessageFromSlickAI(event.from)) return "Slick AI"
  return authorName
}
